import React, { useContext } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Chip, Divider, Tooltip } from "@mui/material";

import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { ContractStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";

import mainStyles from "../../../styles";
import ZuoraLogoSvg from "assets/zuora-logo";
import SyncZuoraButton from "components/org-display/details-display-cards/sync-zuora-button";
import {
  ContractsListContext,
  OrgDetailsContext,
  SubDataContext,
} from "context/organisation-context";
import { deriveProductStatusForZuora } from "helpers/product-status";
import { getPaymentStatus } from "helpers/raw-subscription-doc";

/**
 * Tier and the three statuses, as pills in the summary strip (FG-8053).
 *
 * Product Status is derived by Periscope; Subscription and Payment Status come
 * straight from Zuora, and the two sides routinely disagree — that disagreement
 * is usually why someone opened the page. Each pill carries a mark saying which
 * side of that line it sits on, and the billing pair is grouped behind a divider.
 */
export interface SummaryStatusPillsProps {
  isZuora: boolean;
  /** Reloads the org after a Zuora sync completes. */
  refreshOrgData: () => void;
}

const SummaryStatusPills = ({
  isZuora,
  refreshOrgData,
}: SummaryStatusPillsProps) => {
  const classes = mainStyles();
  const { subData } = useContext(SubDataContext);
  const { orgDetails } = useContext(OrgDetailsContext);
  const { contractsList, isContractListLoading, contractListError } =
    useContext(ContractsListContext);

  const subscription = subData?.subscription;
  if (!subscription) return null;

  const isEnterprise = Boolean(
    contractsList?.contracts?.some(
      (contract) =>
        contract?.status === ContractStatus.ACTIVATED &&
        contract?.hasEnterprise === true,
    ),
  );
  const tier = isEnterprise ? Tier.ENTERPRISE : subscription.tier;
  const tierDisplay: Record<number, { label: string; style: object }> = {
    [Tier.UNSPECIFIED]: { label: "Unspecified", style: classes.neutral },
    [Tier.FREE]: { label: "Free", style: classes.neutral },
    [Tier.PREMIUM]: { label: "Premium", style: classes.purpleRain },
    [Tier.ENTERPRISE]: { label: "Enterprise", style: classes.redWine },
  };

  const productStatus = deriveProductStatusForZuora(
    orgDetails?.active,
    subscription.tier,
    contractsList?.contracts,
    isContractListLoading,
    Boolean(contractListError),
  );
  const productStyle: Record<string, object> = {
    Active: classes.greenDay,
    "Active (Trialing)": classes.blueMoon,
    Suspended: classes.redWine,
    Inactive: classes.neutral,
  };

  const stateLabel: Record<number, string> = {
    [SubscriptionState.UNSPECIFIED]: "Unspecified",
    [SubscriptionState.ACTIVE]: "Active",
    [SubscriptionState.TRIALING]: "Trialing",
    [SubscriptionState.PAST_DUE]: "Past Due",
    [SubscriptionState.CANCELED]: "Canceled",
  };
  const stateStyle: Record<number, object> = {
    [SubscriptionState.UNSPECIFIED]: classes.neutral,
    [SubscriptionState.ACTIVE]: classes.greenDay,
    [SubscriptionState.TRIALING]: classes.blueMoon,
    [SubscriptionState.PAST_DUE]: classes.yellowSubmarine,
    [SubscriptionState.CANCELED]: classes.redWine,
  };

  const paymentStatus = getPaymentStatus(subData?.json);
  const paymentStyle: Record<string, object> = {
    Paid: classes.greenDay,
    "Unpaid (Within Terms)": classes.yellowSubmarine,
    "Unpaid (Overdue - Dunning)": classes.orangeCrush,
    "Unpaid (Overdue - Grace Period)": classes.redWine,
  };

  // Sized to sit inside a small Chip without stretching it - the mark is there
  // to say "this came from Zuora", not to be read.
  const zuoraMark = (
    <Box
      component="span"
      sx={{ display: "inline-flex", lineHeight: 0, mr: 0.5, my: -0.5 }}
      aria-hidden
    >
      <ZuoraLogoSvg size={13} />
    </Box>
  );

  // A free org has no Zuora subscription: nothing to sync, and no billing
  // statuses to report. The pane shows a plain logo in that case, so this does
  // too rather than offering a button that would fail.
  const isFreeOrg = subscription.tier === Tier.FREE;
  const syncButton = isFreeOrg ? (
    <Tooltip title="Not billed through Zuora">
      <Box
        component="span"
        sx={{ display: "inline-flex", lineHeight: 0, p: 0.25 }}
        data-testid="summary-zuora-logo"
      >
        <ZuoraLogoSvg size={30} />
      </Box>
    </Tooltip>
  ) : (
    // The same flow the subscription pane offers, confirmation and account
    // override included - the strip is just a second door onto it.
    <SyncZuoraButton
      subscriptionId={subscription.subscriptionId}
      refreshOrgData={refreshOrgData}
      iconSize={30}
      testId="summary-zuora-sync-btn"
    />
  );

  return (
    <>
      {tier !== undefined && (
        <Chip
          size="small"
          label={tierDisplay[tier].label}
          sx={{ fontWeight: 700, ...tierDisplay[tier].style }}
          data-testid="summary-tier-pill"
        />
      )}

      {productStatus.kind === "resolved" && (
        <Tooltip
          title={`Product status: ${productStatus.status} — derived by Periscope`}
        >
          <Chip
            size="small"
            icon={<CheckCircleIcon sx={{ color: "inherit" }} />}
            label={productStatus.status}
            sx={{
              fontWeight: 700,
              ...productStyle[productStatus.status],
              "& .MuiChip-icon": { color: "#FFFFFF" },
            }}
            data-testid="summary-product-pill"
          />
        </Tooltip>
      )}

      {/* Everything after the divider is billing's view of the same org. */}
      {isZuora && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          {syncButton}
        </>
      )}

      {isZuora && !isFreeOrg && subscription.state !== undefined && (
        <Tooltip
          title={`Subscription status in Zuora: ${stateLabel[subscription.state]}`}
        >
          <Chip
            size="small"
            label={
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                {zuoraMark}
                {stateLabel[subscription.state]}
              </Box>
            }
            sx={{ fontWeight: 700, ...stateStyle[subscription.state] }}
            data-testid="summary-subscription-pill"
          />
        </Tooltip>
      )}

      {isZuora && !isFreeOrg && paymentStatus && (
        <Tooltip title={`Payment status in Zuora: ${paymentStatus}`}>
          <Chip
            size="small"
            label={
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                {zuoraMark}
                {paymentStatus}
              </Box>
            }
            sx={{
              fontWeight: 700,
              ...(paymentStyle[paymentStatus] ?? classes.neutral),
            }}
            data-testid="summary-payment-pill"
          />
        </Tooltip>
      )}
    </>
  );
};

export default SummaryStatusPills;
